/**
 * Scope 3 Screening & Prioritization Engine (ERS-S3)
 * GHG Protocol Aligned - Categories 1, 2, 4, 5, 6, 7
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import {
  TrendingUp, Package, Building, Truck, Trash2, Plane,
  AlertCircle, Sparkles, Plus, BarChart3, Target, Download, Upload
} from 'lucide-react';
import { useAlert } from '../../context/AlertContext';

const API = process.env.REACT_APP_BACKEND_URL;

const Scope3Module = ({ study }) => {
  const { showSuccess, showError, showWarning } = useAlert();
  const [activeCategory, setActiveCategory] = useState('overview');
  const [hotspotData, setHotspotData] = useState(null);
  const [aiNarrative, setAiNarrative] = useState(null);
  const [generatingNarrative, setGeneratingNarrative] = useState(false);
  const [categoryData, setCategoryData] = useState({
    purchasedGoods: [],
    capitalGoods: [],
    transport: [],
    waste: [],
    travel: []
  });

  useEffect(() => {
    if (study?.id) {
      fetchAllData();
    }
  }, [study?.id]);

  const fetchAllData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [cat1, cat2, cat4, cat5, cat6, hotspot] = await Promise.all([
        axios.get(`${API}/api/scope3/purchased-goods/${study.id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/scope3/capital-goods/${study.id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/scope3/transport/${study.id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/scope3/waste/${study.id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/scope3/travel/${study.id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/scope3/hotspot/${study.id}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setCategoryData({
        purchasedGoods: cat1.data.entries || [],
        capitalGoods: cat2.data.entries || [],
        transport: cat4.data.entries || [],
        waste: cat5.data.entries || [],
        travel: cat6.data.entries || []
      });
      setHotspotData(hotspot.data);
    } catch (error) {
      console.error('Error fetching Scope 3 data:', error);
    }
  };

  const generateNarrative = async () => {
    setGeneratingNarrative(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(`${API}/api/scope3/ai-narrative/${study.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiNarrative(res.data);
    } catch (error) {
      showError('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGeneratingNarrative(false);
    }
  };

  const getCategoryIcon = (category) => {
    if (category === 'purchased') return <Package className="w-5 h-5" />;
    if (category === 'capital') return <Building className="w-5 h-5" />;
    if (category === 'transport') return <Truck className="w-5 h-5" />;
    if (category === 'waste') return <Trash2 className="w-5 h-5" />;
    if (category === 'travel') return <Plane className="w-5 h-5" />;
    return <BarChart3 className="w-5 h-5" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl p-6 text-white">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">Scope 3 Value Chain Screening</h2>
            <p className="text-blue-100">GHG Protocol Categories 1, 2, 4, 5, 6, 7</p>
          </div>
          <button
            onClick={generateNarrative}
            disabled={generatingNarrative}
            className="px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 font-medium flex items-center gap-2 disabled:opacity-50"
          >
            <Sparkles className="w-4 h-4" />
            {generatingNarrative ? 'Generating...' : 'AI Summary'}
          </button>
        </div>

        {hotspotData && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-white/20 rounded-lg p-3">
              <p className="text-xs text-blue-100">Total Scope 3</p>
              <p className="text-2xl font-bold">{hotspotData.total_scope3_emissions}</p>
              <p className="text-xs text-blue-100">tCO₂e</p>
            </div>
            <div className="bg-white/20 rounded-lg p-3">
              <p className="text-xs text-blue-100">Categories</p>
              <p className="text-2xl font-bold">6</p>
              <p className="text-xs text-blue-100">assessed</p>
            </div>
            <div className="bg-white/20 rounded-lg p-3">
              <p className="text-xs text-blue-100">Data Points</p>
              <p className="text-2xl font-bold">
                {Object.values(categoryData).reduce((sum, arr) => sum + arr.length, 0)}
              </p>
              <p className="text-xs text-blue-100">entries</p>
            </div>
            <div className="bg-white/20 rounded-lg p-3">
              <p className="text-xs text-blue-100">Materiality</p>
              <p className="text-2xl font-bold">
                {hotspotData.categories?.find(c => c.share_pct > 40) ? '🔴 High' : 
                 hotspotData.categories?.find(c => c.share_pct > 20) ? '🟠 Med' : '🟢 Low'}
              </p>
              <p className="text-xs text-blue-100">top category</p>
            </div>
          </div>
        )}
      </div>

      {/* Mandatory Disclaimer */}
      <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
          <div className="text-sm text-orange-900">
            <p className="font-semibold mb-1">Screening-Grade Estimates</p>
            <p>Scope 3 values are screening-grade estimates intended for prioritization, not external assurance. Use for management decisions and reduction planning.</p>
          </div>
        </div>
      </div>

      {/* AI Narrative */}
      {aiNarrative && (
        <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6">
          <h3 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            AI Executive Summary
          </h3>
          <div className="prose prose-sm max-w-none text-gray-700">
            <ReactMarkdown>{typeof aiNarrative.narrative === 'string' ? aiNarrative.narrative : JSON.stringify(aiNarrative.narrative)}</ReactMarkdown>
          </div>
        </div>
      )}

      {/* Category Navigation */}
      <div className="bg-white rounded-xl border-2 border-gray-200 p-2 shadow-sm">
        <div className="flex gap-1 flex-wrap">
          <button
            onClick={() => setActiveCategory('overview')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeCategory === 'overview' ? 'bg-blue-600 text-white shadow-md' : 'bg-transparent text-gray-700 hover:bg-gray-100'
            }`}
          >
            <BarChart3 className="w-4 h-4 inline mr-2" />
            Hotspot View
          </button>
          <button
            onClick={() => setActiveCategory('purchased')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeCategory === 'purchased' ? 'bg-blue-600 text-white shadow-md' : 'bg-transparent text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Package className="w-4 h-4 inline mr-2" />
            Cat 1: Purchased Goods
          </button>
          <button
            onClick={() => setActiveCategory('capital')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeCategory === 'capital' ? 'bg-blue-600 text-white shadow-md' : 'bg-transparent text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Building className="w-4 h-4 inline mr-2" />
            Cat 2: Capital Goods
          </button>
          <button
            onClick={() => setActiveCategory('transport')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeCategory === 'transport' ? 'bg-blue-600 text-white shadow-md' : 'bg-transparent text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Truck className="w-4 h-4 inline mr-2" />
            Cat 4: Transport
          </button>
          <button
            onClick={() => setActiveCategory('waste')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeCategory === 'waste' ? 'bg-blue-600 text-white shadow-md' : 'bg-transparent text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Trash2 className="w-4 h-4 inline mr-2" />
            Cat 5: Waste
          </button>
          <button
            onClick={() => setActiveCategory('travel')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeCategory === 'travel' ? 'bg-blue-600 text-white shadow-md' : 'bg-transparent text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Plane className="w-4 h-4 inline mr-2" />
            Cat 6/7: Travel
          </button>
        </div>
      </div>

      {/* Hotspot View */}
      {activeCategory === 'overview' && hotspotData && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Scope 3 Materiality Heatmap</h3>
            
            <div className="space-y-3">
              {hotspotData.categories?.map((cat, i) => (
                <div key={i} className={`border-2 rounded-lg p-4 bg-${cat.color}-50 border-${cat.color}-300`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">{cat.category}</h4>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium bg-${cat.color}-100 text-${cat.color}-700`}>
                      {cat.materiality}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{cat.emissions_tco2e} tCO₂e</p>
                      <p className="text-sm text-gray-600">Share: {cat.share_pct}%</p>
                    </div>
                    <div className="w-32 bg-gray-200 rounded-full h-3">
                      <div 
                        className={`bg-${cat.color}-500 h-3 rounded-full transition-all`}
                        style={{width: `${Math.min(cat.share_pct, 100)}%`}}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900 font-semibold mb-2">Total Scope 3 Emissions: {hotspotData.total_scope3_emissions} tCO₂e</p>
              <p className="text-xs text-blue-800">{hotspotData.disclaimer}</p>
            </div>
          </div>
        </div>
      )}

      {/* Category 1: Purchased Goods */}
      {activeCategory === 'purchased' && (
        <CategoryPurchasedGoods 
          studyId={study.id} 
          data={categoryData.purchasedGoods}
          onRefresh={fetchAllData}
        />
      )}

      {/* Category 2: Capital Goods */}
      {activeCategory === 'capital' && (
        <CategoryCapitalGoods 
          studyId={study.id} 
          data={categoryData.capitalGoods}
          onRefresh={fetchAllData}
        />
      )}

      {/* Category 4: Transport */}
      {activeCategory === 'transport' && (
        <CategoryTransport 
          studyId={study.id} 
          data={categoryData.transport}
          onRefresh={fetchAllData}
        />
      )}

      {/* Category 5: Waste */}
      {activeCategory === 'waste' && (
        <CategoryWaste 
          studyId={study.id} 
          data={categoryData.waste}
          onRefresh={fetchAllData}
        />
      )}

      {/* Category 6/7: Travel */}
      {activeCategory === 'travel' && (
        <CategoryTravel 
          studyId={study.id} 
          data={categoryData.travel}
          onRefresh={fetchAllData}
        />
      )}
    </div>
  );
};

// ==================== CATEGORY 1: PURCHASED GOODS ====================
const CategoryPurchasedGoods = ({ studyId, data, onRefresh }) => {
  const { showSuccess, showError, showWarning } = useAlert();
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    material_category: 'Steel',
    material_subtype: '',
    quantity: '',
    unit: 'MT',
    spend_rs: '',
    ef_basis: 'Mass-based'
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/scope3/purchased-goods`, {
        study_id: studyId,
        ...formData,
        quantity: formData.quantity ? parseFloat(formData.quantity) : null,
        spend_rs: formData.spend_rs ? parseFloat(formData.spend_rs) : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Entry added successfully!');
      setShowAddForm(false);
      setFormData({
        material_category: 'Steel',
        material_subtype: '',
        quantity: '',
        unit: 'MT',
        spend_rs: '',
        ef_basis: 'Mass-based'
      });
      onRefresh();
    } catch (error) {
      showError('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = "Material Category,Material Subtype,Quantity (MT),Spend (Rs),Calculation Method (Mass-based/Spend-based)\nSteel,Mild Steel,100,,Mass-based\nAluminium,,50000,Spend-based";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'scope3_purchased_goods_template.csv';
    a.click();
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      const lines = text.split('\n').slice(1); // Skip header
      
      const token = localStorage.getItem('token');
      let successCount = 0;

      for (const line of lines) {
        if (!line.trim()) continue;
        const [material, subtype, qty, spend, method] = line.split(',');
        
        try {
          await axios.post(`${API}/api/scope3/purchased-goods`, {
            study_id: studyId,
            material_category: material?.trim(),
            material_subtype: subtype?.trim() || null,
            quantity: qty?.trim() ? parseFloat(qty) : null,
            spend_rs: spend?.trim() ? parseFloat(spend) : null,
            ef_basis: method?.trim() || 'Mass-based'
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          successCount++;
        } catch (error) {
          console.error('Error adding entry:', error);
        }
      }

      showSuccess(`CSV uploaded! ${successCount} entries added.`);
      onRefresh();
    };

    reader.readAsText(file);
  };

  const totalEmissions = data.reduce((sum, e) => sum + e.emissions_tco2e, 0);

  return (
    <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Category 1: Purchased Goods & Services</h3>
          <p className="text-sm text-gray-600 mt-1">Raw materials and consumables (Mass-based or Spend-based)</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={downloadTemplate}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Template
          </button>
          <label className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 cursor-pointer">
            <Upload className="w-4 h-4" />
            Upload CSV
            <input
              type="file"
              accept=".csv"
              onChange={handleCSVUpload}
              className="hidden"
            />
          </label>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Material
          </button>
        </div>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Material Category *</label>
              <select
                value={formData.material_category}
                onChange={(e) => setFormData({...formData, material_category: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="Steel">Steel</option>
                <option value="Aluminium">Aluminium</option>
                <option value="Cement">Cement</option>
                <option value="Plastics">Plastics</option>
                <option value="Paper">Paper</option>
                <option value="Chemicals">Chemicals</option>
                <option value="Textiles">Textiles</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subtype</label>
              <input
                type="text"
                value={formData.material_subtype}
                onChange={(e) => setFormData({...formData, material_subtype: e.target.value})}
                placeholder="e.g., Mild Steel, Hot Rolled"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Calculation Method *</label>
              <select
                value={formData.ef_basis}
                onChange={(e) => setFormData({...formData, ef_basis: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="Mass-based">Mass-based (High Accuracy)</option>
                <option value="Spend-based">Spend-based (Low Accuracy)</option>
              </select>
            </div>
            {formData.ef_basis === 'Mass-based' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity (MT) *</label>
                <input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                  placeholder="e.g., 100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Spend (₹) *</label>
                <input
                  type="number"
                  value={formData.spend_rs}
                  onChange={(e) => setFormData({...formData, spend_rs: e.target.value})}
                  placeholder="e.g., 1000000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            )}
          </div>
          <div className="flex gap-3 mt-4">
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Adding...' : 'Add Entry'}
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Data Table */}
      {data.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No purchased goods data yet</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Material</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Subtype</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Quantity/Spend</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Method</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Emissions (tCO₂e)</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Data Quality</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{entry.material_category}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{entry.material_subtype || '-'}</td>
                  <td className="px-4 py-3 text-center text-sm">
                    {entry.quantity ? `${entry.quantity} ${entry.unit}` : `₹${entry.spend_rs?.toLocaleString()}`}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      entry.ef_basis === 'Mass-based' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                    }`}>
                      {entry.ef_basis}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center font-semibold text-gray-900">{entry.emissions_tco2e}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      entry.data_quality === 'High' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                    }`}>
                      {entry.data_quality}
                    </span>
                  </td>
                </tr>
              ))}
              <tr className="bg-blue-50 font-bold">
                <td colSpan="4" className="px-4 py-3 text-right">Total Category 1:</td>
                <td className="px-4 py-3 text-center text-blue-900">{totalEmissions.toFixed(2)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ==================== CATEGORY 2: CAPITAL GOODS ====================
const CategoryCapitalGoods = ({ studyId, data, onRefresh }) => {
  const { showSuccess, showError, showWarning } = useAlert();
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    asset_type: 'Industrial Machinery',
    capex_rs: '',
    expected_life_years: '10',
    capacity: '',
    capacity_unit: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!formData.capex_rs || !formData.expected_life_years) {
      showWarning('Please fill in Capex and Asset Life');
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/scope3/capital-goods`, {
        study_id: studyId,
        ...formData,
        capex_rs: parseFloat(formData.capex_rs),
        expected_life_years: parseInt(formData.expected_life_years),
        capacity: formData.capacity ? parseFloat(formData.capacity) : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Capital goods added successfully!');
      setShowAddForm(false);
      setFormData({
        asset_type: 'Industrial Machinery',
        capex_rs: '',
        expected_life_years: '10',
        capacity: '',
        capacity_unit: ''
      });
      onRefresh();
    } catch (error) {
      showError('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const totalAnnual = data.reduce((sum, e) => sum + e.annual_emissions_tco2e, 0);

  return (
    <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Category 2: Capital Goods</h3>
          <p className="text-sm text-gray-600 mt-1">Major assets (Boilers, Machinery) - Annualized emissions</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Asset
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Asset Type *</label>
              <select
                value={formData.asset_type}
                onChange={(e) => setFormData({...formData, asset_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="Industrial Machinery">Industrial Machinery</option>
                <option value="Boilers">Boilers</option>
                <option value="Electrical Equipment">Electrical Equipment</option>
                <option value="Buildings">Buildings</option>
                <option value="Vehicles">Vehicles</option>
                <option value="IT Equipment">IT Equipment</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Capex (₹) *</label>
              <input
                type="number"
                value={formData.capex_rs}
                onChange={(e) => setFormData({...formData, capex_rs: e.target.value})}
                placeholder="e.g., 5000000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expected Life (Years) *</label>
              <input
                type="number"
                value={formData.expected_life_years}
                onChange={(e) => setFormData({...formData, expected_life_years: e.target.value})}
                placeholder="e.g., 10"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Capacity (Optional)</label>
              <input
                type="number"
                value={formData.capacity}
                onChange={(e) => setFormData({...formData, capacity: e.target.value})}
                placeholder="e.g., 500"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Adding...' : 'Calculate & Add'}
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Data Table */}
      {data.length === 0 ? (
        <div className="text-center py-12">
          <Building className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No capital goods data yet</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Asset Type</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Capex (₹)</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Life (Years)</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Total Emissions</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Annual Emissions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{entry.asset_type}</td>
                  <td className="px-4 py-3 text-center text-sm">₹{entry.capex_rs?.toLocaleString()}</td>
                  <td className="px-4 py-3 text-center text-sm">{entry.expected_life_years}</td>
                  <td className="px-4 py-3 text-center text-sm">{entry.total_emissions_tco2e} tCO₂e</td>
                  <td className="px-4 py-3 text-center font-semibold text-gray-900">{entry.annual_emissions_tco2e} tCO₂e</td>
                </tr>
              ))}
              <tr className="bg-blue-50 font-bold">
                <td colSpan="4" className="px-4 py-3 text-right">Total Annual Emissions:</td>
                <td className="px-4 py-3 text-center text-blue-900">{totalAnnual.toFixed(2)} tCO₂e</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ==================== CATEGORY 4: TRANSPORT ====================
const CategoryTransport = ({ studyId, data, onRefresh }) => {
  const { showSuccess, showError, showWarning } = useAlert();
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    mode: 'Road - Heavy Truck',
    distance_km: '',
    weight_tonnes: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!formData.distance_km || !formData.weight_tonnes) {
      showWarning('Please fill in all fields');
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/scope3/transport`, {
        study_id: studyId,
        mode: formData.mode,
        distance_km: parseFloat(formData.distance_km),
        weight_tonnes: parseFloat(formData.weight_tonnes)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Transport entry added!');
      setShowAddForm(false);
      setFormData({ mode: 'Road - Heavy Truck', distance_km: '', weight_tonnes: '' });
      onRefresh();
    } catch (error) {
      showError('Error: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const totalEmissions = data.reduce((sum, e) => sum + e.emissions_tco2e, 0);

  return (
    <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Category 4: Upstream Transport & Distribution</h3>
          <p className="text-sm text-gray-600 mt-1">Logistics by mode (Road/Rail/Sea/Air) - t-km based</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Route
        </button>
      </div>

      {showAddForm && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Transport Mode *</label>
              <select
                value={formData.mode}
                onChange={(e) => setFormData({...formData, mode: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="Road - Heavy Truck">Road - Heavy Truck</option>
                <option value="Road - Light Truck">Road - Light Truck</option>
                <option value="Rail - Freight">Rail - Freight</option>
                <option value="Sea - Container Ship">Sea - Container Ship</option>
                <option value="Air - Cargo">Air - Cargo</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Distance (km) *</label>
              <input
                type="number"
                value={formData.distance_km}
                onChange={(e) => setFormData({...formData, distance_km: e.target.value})}
                placeholder="e.g., 500"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Weight (Tonnes) *</label>
              <input
                type="number"
                value={formData.weight_tonnes}
                onChange={(e) => setFormData({...formData, weight_tonnes: e.target.value})}
                placeholder="e.g., 50"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={handleSubmit} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Adding...' : 'Add Entry'}
            </button>
            <button onClick={() => setShowAddForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          </div>
        </div>
      )}

      {data.length === 0 ? (
        <div className="text-center py-12">
          <Truck className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No transport data yet</p>
        </div>
      ) : (
        <div>
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Mode</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Distance (km)</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Weight (t)</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">t-km</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Emissions (tCO₂e)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{entry.mode}</td>
                  <td className="px-4 py-3 text-center text-sm">{entry.distance_km}</td>
                  <td className="px-4 py-3 text-center text-sm">{entry.weight_tonnes}</td>
                  <td className="px-4 py-3 text-center text-sm font-medium">{(entry.distance_km * entry.weight_tonnes).toFixed(0)}</td>
                  <td className="px-4 py-3 text-center font-semibold text-gray-900">{entry.emissions_tco2e}</td>
                </tr>
              ))}
              <tr className="bg-blue-50 font-bold">
                <td colSpan="4" className="px-4 py-3 text-right">Total Category 4:</td>
                <td className="px-4 py-3 text-center text-blue-900">{totalEmissions.toFixed(2)} tCO₂e</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ==================== CATEGORY 5: WASTE ====================
const CategoryWaste = ({ studyId, data, onRefresh }) => {
  const { showSuccess, showError, showWarning } = useAlert();
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    waste_type: 'General Waste',
    quantity_kg: '',
    treatment: 'Landfill'
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!formData.quantity_kg) {
      showWarning('Please enter quantity');
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/scope3/waste`, {
        study_id: studyId,
        waste_type: formData.waste_type,
        quantity_kg: parseFloat(formData.quantity_kg),
        treatment: formData.treatment
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Waste entry added!');
      setShowAddForm(false);
      setFormData({ waste_type: 'General Waste', quantity_kg: '', treatment: 'Landfill' });
      onRefresh();
    } catch (error) {
      showError('Error: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const totalEmissions = data.reduce((sum, e) => sum + e.emissions_tco2e, 0);

  return (
    <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Category 5: Waste Generated (Offsite)</h3>
          <p className="text-sm text-gray-600 mt-1">Waste treatment emissions</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Waste
        </button>
      </div>

      {showAddForm && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Waste Type</label>
              <input
                type="text"
                value={formData.waste_type}
                onChange={(e) => setFormData({...formData, waste_type: e.target.value})}
                placeholder="e.g., General Waste"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quantity (kg) *</label>
              <input
                type="number"
                value={formData.quantity_kg}
                onChange={(e) => setFormData({...formData, quantity_kg: e.target.value})}
                placeholder="e.g., 10000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Treatment *</label>
              <select
                value={formData.treatment}
                onChange={(e) => setFormData({...formData, treatment: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="Landfill">Landfill</option>
                <option value="Incineration">Incineration</option>
                <option value="Recycling - Metal">Recycling - Metal</option>
                <option value="Recycling - Paper">Recycling - Paper</option>
                <option value="Recycling - Plastic">Recycling - Plastic</option>
                <option value="Composting">Composting</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={handleSubmit} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Adding...' : 'Add Entry'}
            </button>
            <button onClick={() => setShowAddForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          </div>
        </div>
      )}

      {data.length === 0 ? (
        <div className="text-center py-12">
          <Trash2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No waste data yet</p>
        </div>
      ) : (
        <table className="w-full">
          <thead className="bg-gray-50 border-b-2 border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Waste Type</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Quantity (kg)</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Treatment</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Emissions (tCO₂e)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.map((entry) => (
              <tr key={entry.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-900">{entry.waste_type}</td>
                <td className="px-4 py-3 text-center text-sm">{entry.quantity_kg?.toLocaleString()}</td>
                <td className="px-4 py-3 text-center text-sm">{entry.treatment}</td>
                <td className="px-4 py-3 text-center font-semibold text-gray-900">{entry.emissions_tco2e}</td>
              </tr>
            ))}
            <tr className="bg-blue-50 font-bold">
              <td colSpan="3" className="px-4 py-3 text-right">Total Category 5:</td>
              <td className="px-4 py-3 text-center text-blue-900">{totalEmissions.toFixed(2)} tCO₂e</td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
};

// ==================== CATEGORY 6/7: TRAVEL ====================
const CategoryTravel = ({ studyId, data, onRefresh }) => {
  const { showSuccess, showError, showWarning } = useAlert();
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    mode: 'Flight - Domestic',
    quantity: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!formData.quantity) {
      showWarning('Please enter quantity');
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/scope3/travel`, {
        study_id: studyId,
        mode: formData.mode,
        quantity: parseFloat(formData.quantity)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Travel entry added!');
      setShowAddForm(false);
      setFormData({ mode: 'Flight - Domestic', quantity: '' });
      onRefresh();
    } catch (error) {
      showError('Error: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const totalEmissions = data.reduce((sum, e) => sum + e.emissions_tco2e, 0);

  return (
    <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Category 6 & 7: Business Travel & Commuting</h3>
          <p className="text-sm text-gray-600 mt-1">Flights, hotels, employee commuting</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Travel
        </button>
      </div>

      {showAddForm && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mode *</label>
              <select
                value={formData.mode}
                onChange={(e) => setFormData({...formData, mode: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="Flight - Domestic">Flight - Domestic (km)</option>
                <option value="Flight - International">Flight - International (km)</option>
                <option value="Hotel Night">Hotel Night (nights)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {formData.mode === 'Hotel Night' ? 'Nights' : 'Distance (km)'} *
              </label>
              <input
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                placeholder={formData.mode === 'Hotel Night' ? 'e.g., 10' : 'e.g., 5000'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={handleSubmit} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Adding...' : 'Add Entry'}
            </button>
            <button onClick={() => setShowAddForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          </div>
        </div>
      )}

      {data.length === 0 ? (
        <div className="text-center py-12">
          <Plane className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No travel data yet</p>
        </div>
      ) : (
        <table className="w-full">
          <thead className="bg-gray-50 border-b-2 border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Mode</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Quantity</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Emissions (tCO₂e)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.map((entry) => (
              <tr key={entry.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-900">{entry.mode}</td>
                <td className="px-4 py-3 text-center text-sm">{entry.quantity} {entry.mode === 'Hotel Night' ? 'nights' : 'km'}</td>
                <td className="px-4 py-3 text-center font-semibold text-gray-900">{entry.emissions_tco2e}</td>
              </tr>
            ))}
            <tr className="bg-blue-50 font-bold">
              <td colSpan="2" className="px-4 py-3 text-right">Total Category 6/7:</td>
              <td className="px-4 py-3 text-center text-blue-900">{totalEmissions.toFixed(2)} tCO₂e</td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
};

export default Scope3Module;
